/**
 * Tenant branding bootstrap for the IA portal (Phase H0 hotfix).
 *
 * Bug surface: the IA portal hard-coded `defaultPalette="ocean-breeze"`
 * and rendered a generic `<SidebarBrand title="EduViaOS" />`. A tenant
 * who customised their primary colour + logo via the SA branding
 * editor saw NOTHING of it in their own admin portal. Branding only
 * applied to the public apply surface.
 *
 * Fix: this provider:
 *   1. Calls `useTenantBranding(slug)` once on mount.
 *   2. Writes the BE-returned colours into `--color-primary` /
 *      `--color-secondary` on `document.documentElement` so every
 *      Tailwind class that consumes those vars (button-primary, ring,
 *      sidebar-active, etc.) repaints to the tenant's palette.
 *   3. Surfaces the `logo_url` + `tenant_name` via context so
 *      `<SidebarBrand>` can render the tenant's mark instead of the
 *      hard-coded EduViaOS wordmark.
 *
 * The provider is a no-op on cold load (branding hasn't fetched yet)
 * — the IA shell renders its default palette, then the moment the
 * fetch resolves, the CSS vars flip in place. The transition is
 * imperceptible because TanStack Query backs the call with the
 * 5-minute staleTime configured in `lib/branding/api.ts`.
 *
 * On unmount we restore the previous CSS-var values so a sign-out
 * (which also unmounts this provider) doesn't leak the previous
 * tenant's colours into the login screen.
 */

import * as React from "react";
import { useTenantBranding } from "@/lib/branding/api";
import { TenantBrandingContext, type TenantBrandingContextValue } from "./useTenantBrandingContext";

interface TenantBrandingProviderProps {
  slug: string | null | undefined;
  children: React.ReactNode;
}

/**
 * Read-only branding provider — applies the BE-returned palette to
 * the document root and surfaces logo / favicon to descendants.
 *
 * The provider must wrap `<AppShell>` (logo + sidebar) but can sit
 * INSIDE `<AuthProvider>` so we have a resolved `slug`. See `App.tsx`
 * for the wiring order.
 */
export function TenantBrandingProvider({ slug, children }: TenantBrandingProviderProps) {
  const { data: branding, isLoading } = useTenantBranding(slug);

  React.useEffect(() => {
    if (!branding) return;
    const root = document.documentElement;
    const previous = {
      primary: root.style.getPropertyValue("--color-primary"),
      secondary: root.style.getPropertyValue("--color-secondary"),
    };
    if (branding.primary_color) {
      root.style.setProperty("--color-primary", branding.primary_color);
    }
    if (branding.secondary_color) {
      root.style.setProperty("--color-secondary", branding.secondary_color);
    }
    if (branding.favicon_url) {
      const link =
        document.querySelector<HTMLLinkElement>("link[rel='icon']") ??
        Object.assign(document.createElement("link"), { rel: "icon" });
      link.href = branding.favicon_url;
      if (!link.parentNode) document.head.appendChild(link);
    }
    return () => {
      if (previous.primary) {
        root.style.setProperty("--color-primary", previous.primary);
      } else {
        root.style.removeProperty("--color-primary");
      }
      if (previous.secondary) {
        root.style.setProperty("--color-secondary", previous.secondary);
      } else {
        root.style.removeProperty("--color-secondary");
      }
    };
  }, [branding]);

  const value = React.useMemo<TenantBrandingContextValue>(
    () => ({ branding, isLoading }),
    [branding, isLoading],
  );

  return <TenantBrandingContext.Provider value={value}>{children}</TenantBrandingContext.Provider>;
}
